/**
 * One-time migration cleanup for the Flutter app's root-scoped service worker.
 *
 * The Flutter app registered `/flutter_service_worker.js` at the origin root.
 * That registration survives a Hosting release swap, so React must retire it
 * when a returning user first opens the new app on the same hostname.
 */

export const LEGACY_FLUTTER_SERVICE_WORKER_PATH = '/flutter_service_worker.js';

const CLEANUP_RELOAD_KEY = 'aligned.legacyFlutterServiceWorkerCleanup';
const LEGACY_CACHE_NAMES = new Set([
  'aligned-app-cache',
  'aligned-runtime-cache',
]);

function serviceWorkerScriptUrls(registration: ServiceWorkerRegistration): string[] {
  return [
    registration.active?.scriptURL,
    registration.installing?.scriptURL,
    registration.waiting?.scriptURL,
  ].filter((url): url is string => typeof url === 'string');
}

function isLegacyFlutterRegistration(registration: ServiceWorkerRegistration): boolean {
  return serviceWorkerScriptUrls(registration).some((scriptUrl) => {
    try {
      const parsed = new URL(scriptUrl);
      return parsed.origin === window.location.origin
        && parsed.pathname === LEGACY_FLUTTER_SERVICE_WORKER_PATH;
    } catch {
      return false;
    }
  });
}

function hasRetriedCleanup(): boolean {
  try {
    return window.sessionStorage.getItem(CLEANUP_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

function markCleanupRetried(): void {
  try {
    window.sessionStorage.setItem(CLEANUP_RELOAD_KEY, '1');
  } catch {
    // Private browsing and blocked storage should not prevent app startup.
  }
}

function clearCleanupRetryMarker(): void {
  try {
    window.sessionStorage.removeItem(CLEANUP_RELOAD_KEY);
  } catch {
    // Ignore storage failures during migration cleanup.
  }
}

/**
 * Retire the legacy Flutter worker and its known Cache Storage entries.
 *
 * Returns true only when it initiated a one-time reload. The caller should
 * skip rendering during that pass so the next document is no longer
 * controlled by the old worker.
 */
export async function retireLegacyFlutterServiceWorker(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  try {
    const registrations = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistrations()
      : [];
    const legacyRegistrations = registrations.filter(isLegacyFlutterRegistration);

    const cacheNames = 'caches' in window ? await window.caches.keys() : [];
    const legacyCaches = cacheNames.filter((name) => LEGACY_CACHE_NAMES.has(name));
    const needsCleanup = legacyRegistrations.length > 0 || legacyCaches.length > 0;

    if (!needsCleanup) {
      clearCleanupRetryMarker();
      return false;
    }

    await Promise.all(legacyRegistrations.map((registration) => registration.unregister()));
    await Promise.all(legacyCaches.map((name) => window.caches.delete(name)));

    if (!hasRetriedCleanup()) {
      markCleanupRetried();
      window.location.reload();
      return true;
    }

    clearCleanupRetryMarker();
    return false;
  } catch {
    // Service-worker APIs are best-effort. A failed cleanup must not block the
    // application from loading or signing users in.
    return false;
  }
}
